import React, { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { NavigationConfig } from './types';
import { useSidebarStore } from './sidebar-store';
import { AppSidebarSection } from './app-sidebar-section';
import { AppSidebarItem } from './app-sidebar-item';
import { isRouteActive } from './utils/route-matcher';
import { ANIMATION_DURATION } from './constants';
import { UILogo } from './ui-logo';
import './sidebar.css';

/** Collapse/expand toggle button for the sidebar */
function SidebarToggler() {
  const { isCollapsed, toggleCollapsed } = useSidebarStore();
  const togglerClass = [
    'sidebar-toggler',
    isCollapsed && 'sidebar-toggler--collapsed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button onClick={toggleCollapsed} className={togglerClass}>
      <FontAwesomeIcon
        icon={faChevronLeft}
        className="sidebar-toggler__icon"
        aria-hidden="true"
      />
      <span className="sidebar-toggler__label">Collapse</span>
    </button>
  );
}

interface AppSidebarProps {
  config: NavigationConfig;
  className?: string;
  logo?: ReactNode;
  collapsedLogo?: ReactNode;
  logoHref?: string;
  collapseButton?: ReactNode | null;
  tenantId?: string;
}

export function AppSidebar({
  config,
  className,
  logo,
  collapsedLogo,
  logoHref,
  collapseButton,
}: AppSidebarProps) {
  const { isCollapsed, suppressTransition } = useSidebarStore();
  const location = useLocation();
  const pathname = location.pathname;

  const sidebarClass = `sidebar ${isCollapsed ? 'sidebar--collapsed' : ''} ${
    suppressTransition ? 'sidebar--snap' : ''
  } ${className || ''}`.trim();

  const logoSection = logo ? (
    <div className="sidebar__logo">
      <UILogo
        logo={logo}
        collapsedLogo={collapsedLogo}
        href={logoHref}
        isCollapsed={isCollapsed}
        style={{ flexShrink: 0, transition: `opacity ${ANIMATION_DURATION}ms` }}
      />
    </div>
  ) : null;

  const sections = useMemo(() => {
    // Filter out empty sections and determine where to show dividers
    const validSections = config.sections.filter(
      (section) => section.items && section.items.length > 0
    );

    return (
      <div className="sidebar__sections">
        {validSections.map((section, index) => {
          // Show divider if:
          // 1. Sidebar is collapsed AND
          // 2. Not the first section AND
          // 3. Either current or previous section has a label (not a standalone unlabeled section)
          const showDivider =
            isCollapsed &&
            index > 0 &&
            (section.label || validSections[index - 1]?.label);

          return (
            <div key={index}>
              {showDivider && <div className="sidebar__divider" />}
              <AppSidebarSection
                section={section}
                isCollapsed={isCollapsed}
                pathname={pathname}
              />
            </div>
          );
        })}
      </div>
    );
  }, [config.sections, isCollapsed, pathname]);

  const standaloneLinks = useMemo(() => {
    if (!config.standaloneLinks || config.standaloneLinks.length === 0)
      return null;

    return (
      <div className="sidebar__standalone">
        {config.standaloneLinks.map((item) => {
          const isActive = isRouteActive(
            pathname,
            item.href,
            item.activeMatcher
          );
          return (
            <AppSidebarItem
              key={item.href}
              item={item}
              isActive={isActive}
              isCollapsed={isCollapsed}
            />
          );
        })}
      </div>
    );
  }, [config.standaloneLinks, pathname, isCollapsed]);

  const bottomLinks = useMemo(() => {
    if (!config.bottomLinks || config.bottomLinks.length === 0) return null;
    return (
      <AppSidebarSection
        items={config.bottomLinks}
        isCollapsed={isCollapsed}
        pathname={pathname}
        showBorder={true}
        className="sidebar-section--bottom"
      />
    );
  }, [config.bottomLinks, isCollapsed, pathname]);

  const footer =
    collapseButton !== null ? (
      <div className="sidebar__footer">
        {collapseButton || <SidebarToggler />}
      </div>
    ) : null;

  return (
    <aside className={sidebarClass}>
      <div className="sidebar__container">
        {logoSection}
        <nav className="sidebar__nav">
          {sections}
          {standaloneLinks}
          {bottomLinks}
        </nav>
        {footer}
      </div>
    </aside>
  );
}
